import { Dropdown } from 'primereact/dropdown';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import InputStyle from '../style.ts';
import { defaultSortFunc } from '../utils.ts';

export default function InputDropdown({
  containerStyle = {},
  editable = false,
  filter = false,
  inputList,
  isEnabled = true,
  isHidden = false,
  isRequired = false,
  isValid = true,
  labelText = '',
  labelStyle = {},
  name,
  onChange,
  options: optionsProp,
  clearOnStatusChange = true,
  sortFunc = defaultSortFunc,
  value = '',
}) {
  useEffect(() => {
    if (clearOnStatusChange && (!isEnabled || isHidden)) {
      onChange({ [name]: null });
    }
  }, [isEnabled, isHidden, clearOnStatusChange, onChange, name]);

  if (isHidden) return null;

  let options = inputList
    ? Object.values(inputList)
        .filter((x) => x)
        .reduce((agg, { groupLabel, ...item }) => {
          if (!groupLabel) return [...agg, item];

          const group = agg.find((g) => g.groupLabel === groupLabel);

          if (group) group.items.push(item);
          else agg.push({ groupLabel, items: [item] });

          return agg;
        }, [])
    : optionsProp.map((el) =>
        typeof el === 'object' ? el : { label: el, value: el },
      );

  options.forEach((o) => {
    if (o.groupLabel) {
      o.label = o.groupLabel;
    }
  });

  // Caller can provide numerical 'sortOrder' on the options, which overrides any sortFunc provided
  if (options[0]?.sortOrder || options[0]?.sortOrder === 0) {
    options.sort((a, b) => a.sortOrder - b.sortOrder);
  } else {
    options.sort(sortFunc);
  }

  function handleChange(e) {
    if (e.target.value === undefined) {
      onChange?.({ [name]: null });
    } else if (Object.hasOwn(e.target.value, 'value')) {
      onChange?.({ [name]: e.target.value.value });
    } else {
      onChange?.({ [name]: e.target.value });
    }
  }
  const groupProps = {
    optionGroupChildren: 'items',
    optionGroupLabel: 'groupLabel',
  };

  return (
    <span style={{ ...InputStyle.container, ...containerStyle }}>
      <label
        htmlFor={name}
        style={{ ...InputStyle.label(isEnabled), ...labelStyle }}
      >
        {labelText}
        {isRequired && <span style={{ color: 'red' }}>*</span>}
      </label>
      <Dropdown
        className={isValid ? '' : 'p-invalid'}
        disabled={!isEnabled}
        editable={editable}
        filter={filter}
        id={name}
        options={options}
        value={value}
        showClear
        optionLabel="label"
        optionGroupTemplate={(option) => <span>{option.label}</span>}
        {...(options[0]?.groupLabel ? groupProps : {})}
        onChange={handleChange}
      />
    </span>
  );
}

const optionPropShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  sortOrder: PropTypes.number,
  value: PropTypes.any.isRequired,
});

const groupedOptionPropShape = PropTypes.shape({
  groupLabel: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(optionPropShape),
});

InputDropdown.propTypes = {
  containerStyle: PropTypes.object,
  editable: PropTypes.bool,
  filter: PropTypes.bool,
  inputList: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      sortOrder: PropTypes.number,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    }).isRequired,
  ),
  isEnabled: PropTypes.bool,
  isHidden: PropTypes.bool,
  isValid: PropTypes.bool,
  labelText: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      optionPropShape,
      PropTypes.string,
      groupedOptionPropShape,
    ]),
  ),
  showConfirmation: PropTypes.bool,
  sortFunc: PropTypes.func,
  value: PropTypes.any,
};
